/**
 * Adds polylines as contour segmentation annotations to a segmentation.
 *
 * Each polyline is associated with a view reference and is added as a new annotation
 * for the specified segment index. The function updates the annotationUIDsMap to include
 * the new annotation UIDs for the segment.
 *
 * @param viewport The Cornerstone3D viewport where the annotation will be added
 * @param annotationUIDsMap Map from segment index to set of annotation UIDs
 * @param segmentationId The ID of the segmentation
 * @param polylinesInfo Array of PolylineInfoWorld ({ polyline, viewReference }) to add
 * @param segmentIndex The segment index to which the polylines belong
 * @returns The updated annotationUIDsMap
 */

import type { Types } from '@cornerstonejs/core';
import { utilities } from '@cornerstonejs/core';
import { addAnnotation } from '../../stateManagement';
import { addChildAnnotation } from '../../stateManagement/annotation/annotationState';
import type { PolylineInfoWorld } from './polylineInfoTypes';
import type { ContourSegmentationAnnotation } from '../../types';
import updateContourPolyline from '../contours/updateContourPolyline';
import { ContourWindingDirection } from '../../types/ContourAnnotation';

const DEFAULT_CONTOUR_SEG_TOOLNAME = 'PlanarFreehandContourSegmentationTool';

export default function addPolylinesToSegmentation(
  viewport: Types.IViewport,
  annotationUIDsMap: Map<number, Set<string>>,
  segmentationId: string,
  polylinesInfo: PolylineInfoWorld[],
  segmentIndex: number
) {
  polylinesInfo.forEach(({ polyline, viewReference, holePolylines }) => {
    if (polyline.length < 3) {
      return;
    }

    const parentAnnotation = createContourSegmentationAnnotation(
      segmentationId,
      segmentIndex,
      viewReference
    );

    addAnnotation(parentAnnotation, viewport.element);

    const parentPolylineCanvas = polyline.map((point) =>
      viewport.worldToCanvas(point)
    );

    updateContourPolyline(
      parentAnnotation,
      {
        points: parentPolylineCanvas,
        closed: true,
        targetWindingDirection: ContourWindingDirection.Clockwise,
      },
      viewport
    );

    const currentSet = annotationUIDsMap?.get(segmentIndex) || new Set();
    currentSet.add(parentAnnotation.annotationUID);
    annotationUIDsMap.set(segmentIndex, currentSet);

    holePolylines?.forEach((holePolyline) => {
      if (holePolyline.length < 3) {
        return;
      }

      const holeAnnotation = createContourSegmentationAnnotation(
        segmentationId,
        segmentIndex,
        viewReference
      );

      addAnnotation(holeAnnotation, viewport.element);
      addChildAnnotation(parentAnnotation, holeAnnotation);

      const holePolylineCanvas = holePolyline.map((point) =>
        viewport.worldToCanvas(point)
      );

      updateContourPolyline(
        holeAnnotation,
        {
          points: holePolylineCanvas,
          closed: true,
          targetWindingDirection: ContourWindingDirection.CounterClockwise,
        },
        viewport
      );
    });
  });
  return annotationUIDsMap;
}

function createContourSegmentationAnnotation(
  segmentationId: string,
  segmentIndex: number,
  viewReference: Types.ViewReference
): ContourSegmentationAnnotation {
  return {
    annotationUID: utilities.uuidv4() as string,
    data: {
      contour: {
        closed: true,
        polyline: [],
      },
      segmentation: {
        segmentationId,
        segmentIndex,
      },
      handles: {},
      cachedStats: {},
    },
    handles: {},
    highlighted: false,
    autoGenerated: false,
    invalidated: false,
    isLocked: false,
    isVisible: true,
    metadata: {
      toolName: DEFAULT_CONTOUR_SEG_TOOLNAME,
      ...viewReference,
    },
  };
}
